import { useState } from 'react'
import type { ReactNode } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@agms/ui'
import { useApplication } from '../../hooks/useApplication'
import type { Application } from '../../lib/api'
import { formatCurrencyWithCentsOrDash } from '../../lib/format'

interface Props {
  application: Application
}

const STEP_LABELS: Record<number, string> = {
  1: 'Agent & Meta',
  2: 'Merchant Info',
  3: 'Business Profile',
  4: 'Banking',
  5: 'Pricing',
  6: 'Ownership',
  7: 'Equipment',
}

function isStepComplete(completedSteps: number, step: number): boolean {
  return (completedSteps & (1 << (step - 1))) !== 0
}

function formatRate(val: number | string | null | undefined): string {
  if (val === null || val === undefined || val === '') return '-'
  const num = typeof val === 'string' ? parseFloat(val) : val
  if (isNaN(num)) return '-'
  return num.toFixed(4)
}

function formatDateTime(val: string | null | undefined): string {
  if (!val) return '-'
  return new Date(val).toLocaleString()
}

function CopyableValue({ value, label = 'value' }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    await navigator.clipboard.writeText(value)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1400)
  }

  return (
    <span className="inline-flex max-w-full items-center gap-2">
      <span className="truncate font-mono text-xs">{value}</span>
      <button
        type="button"
        onClick={copy}
        title={copied ? 'Copied' : `Copy ${label}`}
        aria-label={copied ? `${label} copied` : `Copy ${label}`}
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
      >
        {copied ? (
          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v10a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l2.414 2.414A1 1 0 0118 6.414V17a2 2 0 01-2 2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
          </svg>
        )}
      </button>
    </span>
  )
}

function Row({ label, value }: { label: string; value: ReactNode | null | undefined }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-gray-100 last:border-0">
      <span className="shrink-0 text-sm text-gray-500">{label}</span>
      <span className="min-w-0 text-right text-sm text-gray-900 font-medium">{value || '-'}</span>
    </div>
  )
}

/**
 * Read-only summary of all application data for ops review.
 * Shows saved values across all 7 TurboApp steps.
 */
export function ApplicationDataCard({ application: a }: Props) {
  const [expanded, setExpanded] = useState(false)
  const { data: freshApplication } = useApplication(a.lead_id)
  const application = freshApplication ?? a
  const completedCount = [1, 2, 3, 4, 5, 6, 7].filter((step) =>
    isStepComplete(application.completed_steps, step)
  ).length
  const ownerName = [application.owner_first_name, application.owner_mi, application.owner_last_name].filter(Boolean).join(' ')

  return (
    <Card>
      <CardHeader className="pb-3">
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="flex w-full items-start justify-between gap-4 text-left"
          aria-expanded={expanded}
        >
          <div className="min-w-0">
            <CardTitle>Application Data</CardTitle>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
              <span>{completedCount}/7 steps complete</span>
              <span className="text-gray-300">|</span>
              <span>{application.pricing_option === 'A' ? 'Pass-Thru' : application.pricing_option === 'B' ? 'Flat Rate' : 'No pricing'}</span>
              <span className="text-gray-300">|</span>
              <span>{application.bank_name ? `Bank: ${application.bank_name}` : 'No bank name'}</span>
              <span className="text-gray-300">|</span>
              <span>{ownerName || 'No owner'}</span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <div className="hidden gap-1 sm:flex">
              {[1, 2, 3, 4, 5, 6, 7].map((step) => (
                <span
                  key={step}
                  title={`Step ${step}: ${STEP_LABELS[step]}`}
                  className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${
                    isStepComplete(application.completed_steps, step)
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {step}
                </span>
              ))}
            </div>
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 text-gray-500">
              <svg
                className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </div>
        </button>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-5">
          {/* Status */}
          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Status</h4>
            <Row label="Application ID" value={<CopyableValue value={application.id} />} />
            <Row label="Locked" value={application.is_locked ? 'Yes' : 'No'} />
            <Row label="Submitted to Fiserv" value={application.is_submitted ? 'Yes' : 'No'} />
            <Row label="Fiserv Status" value={application.fiserv_status} />
            <Row label="Fiserv MPA Status" value={application.fiserv_mpa_status} />
            <Row label="Fiserv Last Polled" value={formatDateTime(application.fiserv_last_polled_at)} />
            <Row label="BoldSign Status" value={application.boldsign_job_status} />
            <Row label="ValidiFI Passed" value={application.validifi_passed === null ? '-' : application.validifi_passed ? 'Yes' : 'No'} />
            {application.validifi_avc_score !== null && <Row label="AVC Score" value={String(application.validifi_avc_score)} />}
          </div>

          {(application.fiserv_status_errors?.length || application.fiserv_status_history?.length) ? (
            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">Fiserv Status Details</h4>
              {application.fiserv_status_errors?.length ? (
                <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-3">
                  <div className="text-sm font-semibold text-red-900">Approved with changes / partial boarding issues</div>
                  <ul className="mt-2 space-y-2 text-sm text-red-800">
                    {application.fiserv_status_errors.map((error, index) => (
                      <li key={`${error.name ?? 'error'}-${index}`} className="rounded border border-red-100 bg-white/70 p-2">
                        {error.message || error.name || 'Fiserv returned an unspecified status issue.'}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {application.fiserv_status_history?.length ? (
                <div className="space-y-2">
                  {application.fiserv_status_history.map((status, index) => (
                    <div key={`${status.code ?? 'status'}-${index}`} className="flex items-start justify-between gap-3 rounded border border-gray-100 bg-gray-50 px-3 py-2 text-sm">
                      <div>
                        <div className="font-medium text-gray-900">{status.code || 'Status'}</div>
                        <div className="text-gray-600">{status.information || '-'}</div>
                      </div>
                      <div className="shrink-0 text-right text-xs text-gray-500">{status.timestamp || '-'}</div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

        {/* Merchant Info */}
        <div>
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Merchant</h4>
          <Row label="DBA Name" value={application.dba_name} />
          <Row label="Legal Name" value={application.legal_name} />
          <Row label="Ownership" value={application.ownership_type} />
          <Row label="Email" value={application.business_email} />
          <Row label="Phone" value={application.dba_phone} />
          <Row label="Website" value={application.website} />
          <Row label="MPA Type" value={application.mpa_type} />
          <Row label="Sales Rep ID" value={application.sales_rep_id} />
        </div>

        {/* Business Profile */}
        <div>
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Business</h4>
          <Row label="Years in Business" value={application.years_in_business !== null ? String(application.years_in_business) : null} />
          <Row label="Monthly Volume" value={formatCurrencyWithCentsOrDash(application.monthly_volume)} />
          <Row label="Avg Ticket" value={formatCurrencyWithCentsOrDash(application.avg_ticket)} />
          <Row label="High Ticket" value={formatCurrencyWithCentsOrDash(application.high_ticket)} />
          <Row label="MCC" value={application.mcc_code} />
          <Row label="Card Swiped" value={application.pct_card_swiped !== null ? `${application.pct_card_swiped}%` : null} />
          <Row label="E-commerce" value={application.pct_ecommerce !== null ? `${application.pct_ecommerce}%` : null} />
          <Row label="Card Not Present" value={application.pct_cnp !== null ? `${application.pct_cnp}%` : null} />
          <Row
            label="Recurring Billing"
            value={application.cardholder_billing_recurring === null ? null : application.cardholder_billing_recurring ? 'Yes' : 'No'}
          />
        </div>

        {/* Banking (no sensitive fields) */}
        <div>
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Banking</h4>
          <Row label="Bank Name" value={application.bank_name} />
          <Row label="Special Instructions" value={application.special_instructions} />
        </div>

        {/* Pricing */}
        <div>
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Pricing</h4>
          <Row label="Option" value={application.pricing_option === 'A' ? 'A — Pass-Thru Interchange' : application.pricing_option === 'B' ? 'B — Flat Rate' : null} />
          {application.pricing_option === 'A' && (
            <>
              <Row label="Bankcard Discount" value={formatRate(application.bankcard_discount_rate)} />
              <Row label="Debit Discount" value={formatRate(application.debit_discount_rate)} />
              <Row label="Amex Rate" value={formatRate(application.amex_rate)} />
              <Row label="Discover Rate" value={formatRate(application.discover_rate)} />
              <Row label="Auth Fee" value={formatRate(application.auth_fee)} />
            </>
          )}
          {application.pricing_option === 'B' && (
            <>
              <Row label="Flat Bankcard" value={formatRate(application.flat_rate_bankcard)} />
              <Row label="Flat Debit" value={formatRate(application.flat_rate_debit)} />
              <Row label="Flat Amex" value={formatRate(application.flat_rate_amex)} />
              <Row label="Flat Discover" value={formatRate(application.flat_rate_discover)} />
            </>
          )}
          {application.pricing_option === 'A' && (
            <>
              <Row label="PIN Debit Item" value={formatRate(application.pin_debit_item_rate)} />
              <Row label="PIN Debit Volume" value={formatRate(application.pin_debit_volume_rate)} />
            </>
          )}
          <Row label="EBT FNS ID" value={application.ebt_fns_id} />
          <Row label="Compliance" value={application.compliance_program === 'none' ? '-' : application.compliance_program} />
          {application.compliance_program && application.compliance_program !== 'none' && (
            <>
              <Row label="Compliance Fee %" value={formatRate(application.compliance_fee_pct)} />
              <Row label="Compliance Fee $" value={formatCurrencyWithCentsOrDash(application.compliance_fee_amt)} />
            </>
          )}
          <Row label="Batch Fee" value={formatRate(application.batch_fee)} />
          <Row label="Maintenance Fee" value={formatCurrencyWithCentsOrDash(application.maintenance_fee)} />
        </div>

        {/* Ownership (non-sensitive only) */}
        <div>
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Beneficial Owner</h4>
          <Row label="Name" value={[application.owner_first_name, application.owner_mi, application.owner_last_name].filter(Boolean).join(' ') || null} />
          <Row label="Title" value={application.owner_title} />
          <Row label="Ownership %" value={application.ownership_pct !== null ? `${application.ownership_pct}%` : null} />
          <Row label="Email" value={application.owner_email} />
          <Row label="Phone" value={application.owner_phone} />
          <Row label="Address" value={[application.owner_address, application.owner_city, application.owner_state, application.owner_zip].filter(Boolean).join(', ') || null} />
        </div>

        {/* Equipment */}
        <div>
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Equipment</h4>
          <Row label="Terminal" value={[application.terminal_make, application.terminal_model].filter(Boolean).join(' ') || null} />
          {application.terminal_qty !== null && <Row label="Terminal Qty" value={String(application.terminal_qty)} />}
          <Row label="Terminal Status" value={application.terminal_status} />
          <Row label="POS" value={[application.pos_make, application.pos_model].filter(Boolean).join(' ') || null} />
          <Row label="Mobile" value={[application.mobile_make, application.mobile_model].filter(Boolean).join(' ') || null} />
          <Row label="PIN Pad" value={[application.pinpad_make, application.pinpad_model].filter(Boolean).join(' ') || null} />
          <Row label="NMI Gateway" value={application.nmi_gateway_enabled ? 'Enabled' : 'No'} />
          <Row label="AGMS Pay" value={application.agms_pay_enabled ? 'Enabled' : 'No'} />
          <Row label="Hardware Program" value={application.hardware_program} />
        </div>

        {/* Fiserv tracking */}
        {(application.fiserv_ext_ref_id || application.fiserv_mid || application.fiserv_as_ref_id) && (
          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Fiserv</h4>
            <Row label="Reserved MID" value={application.fiserv_mid ? <CopyableValue value={application.fiserv_mid} label="reserved MID" /> : null} />
            <Row label="Ext Ref ID" value={application.fiserv_ext_ref_id ? <CopyableValue value={application.fiserv_ext_ref_id} label="Ext Ref ID" /> : null} />
            <Row label="AS Ref ID" value={application.fiserv_as_ref_id} />
            <Row label="Submitted At" value={application.fiserv_submitted_at} />
            {application.fiserv_credit_officer_comments && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                <span className="font-medium">Credit Officer Comments:</span>{' '}
                {application.fiserv_credit_officer_comments}
              </div>
            )}
          </div>
        )}
      </CardContent>
      )}
    </Card>
  )
}
